import React, { useEffect, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Minus, Plus, Trash2, ShoppingBag, LogIn, X } from "lucide-react";
import { Button } from "./ui/button";
import { Progress } from "./ui/progress";
import { Separator } from "./ui/separator";
import { useCart, useUI } from "../lib/cart-context";
import { useAuth } from "../lib/auth-context";
import { imageUrl, formatEUR } from "../lib/api";
// Phase 35 — OriginSelector removed: shipping is weight-based (3 €/kg) in
// the envio shop, so the "Compro desde España / desde los campamentos"
// picker no longer applies and was confusing to users.
import CartRecipesSection from "./CartRecipesSection";
import RecoStrip from "./RecoStrip";

/**
 * Phase 20.2 — Cart drop-down unified with `MegaCategoriesPanel`.
 *
 * Same structural pattern as the categories mega-menu: a normal-flow
 * `<div>` rendered right below the header in `AppShell`. Toggles between
 * `max-h-0` (closed, no shadow) and `max-h-[80vh] md:max-h-[70vh]` (open,
 * shadow + bottom border) with a 300ms ease-out `max-height, box-shadow,
 * border-color` transition. No dark backdrop, no scroll-lock, page below
 * stays fully visible and interactive. Mutual exclusion with the
 * mega-menu is enforced by an effect that closes the other panel when
 * this one opens.
 */
export default function CartDrawer() {
  const { cartOpen, setCartOpen, setCategoryDrawerOpen } = useUI();
  const { cart, updateItem, removeItem } = useCart();
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const closeBtnRef = useRef(null);

  // Mutual exclusion — opening the cart closes the mega-menu.
  useEffect(() => {
    if (cartOpen) setCategoryDrawerOpen(false);
  }, [cartOpen, setCategoryDrawerOpen]);

  // Auto-close on route change (matches mega-menu behavior).
  useEffect(() => {
    setCartOpen(false);
  }, [location.pathname, location.search, setCartOpen]);

  // ESC closes; also focus the close button on open.
  useEffect(() => {
    if (!cartOpen) return;
    const onKey = (e) => {
      if (e.key === "Escape") setCartOpen(false);
    };
    window.addEventListener("keydown", onKey);
    const raf = requestAnimationFrame(() => {
      try {
        closeBtnRef.current?.focus({ preventScroll: true });
      } catch {
        /* noop */
      }
    });
    return () => {
      window.removeEventListener("keydown", onKey);
      cancelAnimationFrame(raf);
    };
  }, [cartOpen, setCartOpen]);

  const percent = Math.min(
    100,
    Math.round((cart.subtotal / (cart.min_subtotal || 25)) * 100)
  );
  const remaining = Math.max(0, (cart.min_subtotal || 25) - cart.subtotal);

  const handleCheckout = () => {
    setCartOpen(false);
    navigate("/checkout");
  };

  return (
    <div
      id="mega-cart"
      role="dialog"
      aria-label="Tu carro"
      data-testid="cart-drawer"
      data-open={cartOpen ? "true" : "false"}
      aria-hidden={!cartOpen}
      inert={cartOpen ? undefined : true}
      className={`w-full bg-white overflow-hidden transition-[max-height,box-shadow,border-color] duration-300 ease-out ${
        cartOpen
          ? "max-h-[80vh] md:max-h-[70vh] shadow-[0_8px_16px_-8px_rgba(0,0,0,0.15)] border-b border-neutral-200"
          : "max-h-0 shadow-none border-b border-transparent"
      }`}
    >
      <div className="mx-auto max-w-7xl px-4 sm:px-6 py-6 overflow-y-auto max-h-[80vh] md:max-h-[70vh]">
        <header className="flex items-center justify-between mb-4">
          <h2 className="font-black text-2xl tracking-tight">Tu carro</h2>
          <button
            ref={closeBtnRef}
            type="button"
            onClick={() => setCartOpen(false)}
            data-testid="cart-close-btn"
            aria-label="Cerrar carro"
            className="h-9 w-9 grid place-items-center rounded-full text-neutral-500 hover:bg-neutral-100 hover:text-neutral-900 transition-colors"
          >
            <X className="h-5 w-5" />
          </button>
        </header>

        {cart.items.length === 0 ? (
          <div
            data-testid="cart-empty-state"
            className="flex flex-col items-center justify-center gap-3 py-10 text-center"
          >
            <div className="h-16 w-16 rounded-full bg-neutral-100 grid place-items-center">
              <ShoppingBag className="h-7 w-7 text-neutral-400" />
            </div>
            <p className="font-semibold text-lg">Tu carro está vacío</p>
            <p className="text-sm text-neutral-500 max-w-xs">
              Añade productos para hacer tu compra. El pedido mínimo es de{" "}
              {formatEUR(cart.min_subtotal ?? 25)}.
            </p>
            <Button
              onClick={() => setCartOpen(false)}
              className="mt-2 bg-[#00953B] hover:bg-[#007a30] rounded-full"
              data-testid="cart-empty-start-shopping-btn"
            >
              Empezar a comprar
            </Button>
          </div>
        ) : (
          <div className="grid gap-6 lg:grid-cols-3">
            {/* LEFT — item list (2/3 on lg+, full width below) */}
            <div className="lg:col-span-2 space-y-4">
              {cart.items.map((it) => (
                <div
                  key={it.product_id}
                  data-testid={`cart-item-${it.product_id}`}
                  className="flex gap-3"
                >
                  <div className="h-20 w-20 rounded-lg bg-neutral-50 border border-neutral-100 shrink-0 overflow-hidden">
                    {it.product?.image_url && (
                      <img
                        src={imageUrl(it.product.image_url)}
                        alt={it.product.name}
                        className="h-full w-full object-contain"
                      />
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-xs text-neutral-500">
                      {it.product?.brand}
                    </div>
                    <div className="text-sm font-medium leading-tight line-clamp-2">
                      {it.product?.name}
                    </div>
                    <div className="text-xs text-neutral-500 mt-0.5">
                      {it.product?.unit_label} · {formatEUR(it.product?.price_eur || 0)}
                    </div>
                    <div className="mt-2 flex items-center justify-between">
                      <div className="inline-flex items-center rounded-full border border-neutral-200 overflow-hidden">
                        <button
                          data-testid={`cart-item-decrement-${it.product_id}`}
                          className="h-8 w-8 grid place-items-center hover:bg-neutral-50"
                          onClick={() =>
                            updateItem(it.product_id, Math.max(0, it.quantity - 1))
                          }
                          aria-label="Disminuir"
                        >
                          <Minus className="h-3.5 w-3.5" />
                        </button>
                        <span
                          className="h-8 min-w-[2rem] px-2 grid place-items-center text-sm font-semibold"
                          data-testid={`cart-item-qty-${it.product_id}`}
                        >
                          {it.quantity}
                        </span>
                        <button
                          data-testid={`cart-item-increment-${it.product_id}`}
                          className="h-8 w-8 grid place-items-center hover:bg-neutral-50"
                          onClick={() => updateItem(it.product_id, it.quantity + 1)}
                          aria-label="Aumentar"
                        >
                          <Plus className="h-3.5 w-3.5" />
                        </button>
                      </div>
                      <div className="text-sm font-semibold">
                        {formatEUR(it.line_total)}
                      </div>
                      <button
                        data-testid={`cart-item-remove-${it.product_id}`}
                        onClick={() => removeItem(it.product_id)}
                        aria-label="Eliminar"
                        className="text-neutral-400 hover:text-red-600 p-1"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* RIGHT — sticky summary (1/3 on lg+, stacked below on smaller) */}
            <aside className="lg:col-span-1 lg:sticky lg:top-2 self-start rounded-xl border border-neutral-200 bg-neutral-50 p-4 space-y-3">
              {/* Phase 35 — the OriginSelector block was removed. Envio
                  shipping is now weight-based (3 €/kg, Phase 34) so a
                  per-origin picker is no longer meaningful. */}
              <div>
                <div className="flex justify-between text-xs mb-1">
                  <span
                    data-testid="cart-min-order-label"
                    className={
                      cart.min_order_reached
                        ? "text-[#00953B] font-semibold"
                        : "text-neutral-600"
                    }
                  >
                    {cart.min_order_reached
                      ? "¡Pedido mínimo alcanzado!"
                      : `Añade ${formatEUR(remaining)} más para el pedido mínimo`}
                  </span>
                  <span className="text-neutral-500">
                    {formatEUR(cart.subtotal)} / {formatEUR(cart.min_subtotal)}
                  </span>
                </div>
                <Progress
                  value={percent}
                  className="h-2 [&>div]:bg-[#00953B]"
                  data-testid="cart-min-progress"
                />
              </div>

              <Separator />

              <div className="space-y-1 text-sm">
                <div className="flex justify-between">
                  <span className="text-neutral-600">Subtotal</span>
                  <span data-testid="cart-subtotal">{formatEUR(cart.subtotal)}</span>
                </div>
                {cart.shop === "envio" && cart.total_weight_kg > 0 && (
                  <div className="flex justify-between text-xs text-neutral-500">
                    <span>Peso total</span>
                    <span data-testid="cart-total-weight">
                      {cart.total_weight_kg.toFixed(2)} kg
                    </span>
                  </div>
                )}
                <div className="flex justify-between">
                  <span className="text-neutral-600 truncate">
                    {cart.shipping_label ||
                      (cart.shopper_origin === "spain"
                        ? "Envío (España → campamentos)"
                        : cart.shopper_origin === "camps"
                        ? "Envío (dentro de los campamentos)"
                        : "Envío")}
                  </span>
                  <span data-testid="cart-shipping">{formatEUR(cart.shipping_cost)}</span>
                </div>
                <div className="flex justify-between font-bold text-base pt-1">
                  <span>Total</span>
                  <span data-testid="cart-total">{formatEUR(cart.total)}</span>
                </div>
              </div>

              <Button
                data-testid="cart-checkout-btn"
                disabled={!cart.min_order_reached}
                onClick={handleCheckout}
                className="w-full h-12 rounded-full bg-[#00953B] hover:bg-[#007a30] text-white font-semibold disabled:opacity-50"
              >
                Tramitar pedido
              </Button>
            </aside>
          </div>
        )}

        {/* Phase 22.1 — customer AI blocks. Only render if the cart has items. */}
        {user && cart.items.length > 0 && (
          <>
            <CartRecipesSection onNavigate={() => setCartOpen(false)} />
            <RecoStrip
              context="cart"
              variant="compact"
              testid="reco-strip-cart"
              onNavigate={() => setCartOpen(false)}
            />
          </>
        )}
      </div>
    </div>
  );
}
